// src/components/events/EventRegistrationForm.jsx
import React, { useState } from "react";
import Button from "../common/Button";
import SelectDropdown from "../common/SelectDropdown";
import { Calendar, MapPin, User, Mail, Phone, Heart, Activity } from "lucide-react";

const EventRegistrationForm = ({ event, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    emergencyContact: "",
    experienceLevel: "",
    medicalConditions: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // UI only - log form data for now
    console.log("Registration submitted:", { event: event?.title, ...formData });
    // Show success feedback and close modal
    alert(`Registration submitted for ${event?.title}! (UI Demo)`);
    onClose();
  };

  const inputClasses =
    "w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors duration-300";

  const labelClasses =
    "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2";

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Event Info Summary */}
      {event && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6 border border-gray-200 dark:border-gray-700">
          <h4 className="font-semibold text-gray-800 dark:text-white mb-2">
            {event.title}
          </h4>
          <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
            <span className="flex items-center">
              <Calendar className="w-4 h-4 mr-1 text-cycling-orange" />
              {event.date}
            </span>
            <span className="flex items-center">
              <MapPin className="w-4 h-4 mr-1 text-cycling-green" />
              {event.location}
            </span>
          </div>
        </div>
      )}

      {/* Full Name */}
      <div>
        <label htmlFor="fullName" className={labelClasses}>
          <span className="flex items-center">
            <User className="w-4 h-4 mr-2 text-gray-500" />
            Full Name *
          </span>
        </label>
        <input
          type="text"
          id="fullName"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          placeholder="Enter your full name"
          required
          className={inputClasses}
        />
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className={labelClasses}>
          <span className="flex items-center">
            <Mail className="w-4 h-4 mr-2 text-gray-500" />
            Email Address *
          </span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="your@email.com"
          required
          className={inputClasses}
        />
      </div>

      {/* Phone Number */}
      <div>
        <label htmlFor="phone" className={labelClasses}>
          <span className="flex items-center">
            <Phone className="w-4 h-4 mr-2 text-gray-500" />
            Phone Number *
          </span>
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="+91 XXXXX XXXXX"
          required
          className={inputClasses}
        />
      </div>

      {/* Emergency Contact */}
      <div>
        <label htmlFor="emergencyContact" className={labelClasses}>
          <span className="flex items-center">
            <Heart className="w-4 h-4 mr-2 text-gray-500" />
            Emergency Contact *
          </span>
        </label>
        <input
          type="text"
          id="emergencyContact"
          name="emergencyContact"
          value={formData.emergencyContact}
          onChange={handleChange}
          placeholder="Name and phone number"
          required
          className={inputClasses}
        />
      </div>

      {/* Experience Level */}
      <div>
        <label className={labelClasses}>
          <span className="flex items-center">
            <Activity className="w-4 h-4 mr-2 text-gray-500" />
            Cycling Experience Level *
          </span>
        </label>
        <SelectDropdown
          id="experienceLevel"
          value={formData.experienceLevel}
          onChange={(e) => handleChange({ target: { name: "experienceLevel", value: e.target.value } })}
          placeholder="Select your experience level"
          options={[
            { value: "", label: "Select your experience level" },
            { value: "beginner", label: "Beginner - New to cycling" },
            { value: "intermediate", label: "Intermediate - Regular rider" },
            { value: "advanced", label: "Advanced - Experienced cyclist" },
          ]}
          className="w-full"
        />
      </div>

      {/* Medical Conditions */}
      <div>
        <label htmlFor="medicalConditions" className={labelClasses}>
          Any Medical Conditions (Optional)
        </label>
        <textarea
          id="medicalConditions"
          name="medicalConditions"
          value={formData.medicalConditions}
          onChange={handleChange}
          placeholder="Let us know about any allergies, conditions, or special requirements..."
          rows={3}
          className={inputClasses}
        />
      </div>

      {/* Submit Button */}
      <div className="pt-4">
        <Button type="submit" variant="primary" className="w-full" size="lg">
          Complete Registration
        </Button>
      </div>

      {/* Disclaimer */}
      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
        By registering, you agree to our terms and conditions and acknowledge
        the inherent risks of cycling events.
      </p>
    </form>
  );
};

export default EventRegistrationForm;
